import {
  CarPdfHeader,
  CarPdfHeaderCell,
  CarPdfRow,
  CarPdfTable,
  CarPdfTableProps,
  CarPdfTextCell,
  CarPdfViewCell,
} from "@app/components/PdfTable";
import {
  CarPortfolioAssetColumnEnum,
  CarPortfolioAssetColumnId,
  CarPortfolioAssetModule,
  CarPortfolioAssetRow,
  CarPortfolioAssetTableData,
} from "../usePortfolioGroupVersionData";
import { AssetClassLevel } from "@app/const";
import { formatPercent, isDefined } from "@app/utils";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfTypography } from "@app/components/PdfTypography";
import { Circle, G, Rect, Svg } from "@react-pdf/renderer";
import { useTheme } from "@mui/material";

const getAllocationByColumnId = (
  module: CarPortfolioAssetModule,
  columnId: CarPortfolioAssetColumnId,
) => {
  let value = module.values.find((val) => val.columnId === columnId);

  // this covers the case for "Selection" page. On this page CarPortfolioAssetColumnEnum.selected column not in the list but the data still can be found
  if (!value && columnId === CarPortfolioAssetColumnEnum.selected) {
    value = module.values.find((val) => val.dataId === module.selectedDataId);
  }

  return value?.allocation;
};

export interface AssetAllocationPerModulePdfTableProps {
  style?: CarPdfTableProps["style"];
  columnId: CarPortfolioAssetColumnId;
  level: AssetClassLevel;
  tableData: CarPortfolioAssetTableData;
  hideZeroValues: boolean;
}

export const AssetAllocationPerModulePdfTable = ({
  style,
  columnId,
  level,
  tableData,
  hideZeroValues,
}: AssetAllocationPerModulePdfTableProps) => {
  const theme = useTheme();
  let rows = tableData.assetRows[level];
  if (hideZeroValues) {
    rows = rows.filter((i) =>
      i.modules.some((md) => {
        const value = getAllocationByColumnId(md, columnId) ?? 0;

        return value > 0;
      }),
    );
  }
  let totalRow: CarPortfolioAssetRow | undefined;
  switch (level) {
    case AssetClassLevel.level1:
      totalRow = tableData.assetRows.level1Total;
      break;
    case AssetClassLevel.level2:
      totalRow = tableData.assetRows.level2Total;
      break;
    case AssetClassLevel.level3:
      totalRow = tableData.assetRows.level3Total;
      break;
    case AssetClassLevel.level4:
      totalRow = tableData.assetRows.level4Total;
      break;
    default:
      break;
  }

  return (
    <CarPdfTable
      style={style}
      colWidths={[3, ...tableData.bands.map((i) => 1)]}
      isRounded
      isStriped
    >
      <CarPdfHeader paddingVertical={11} textVariant="v11Regular">
        {[
          <CarPdfHeaderCell key="label" />,
          ...tableData.bands.map((band, idx, arr) => (
            <CarPdfHeaderCell key={band.moduleId}>
              {band.label}
            </CarPdfHeaderCell>
          )),
        ]}
      </CarPdfHeader>

      {[...rows, totalRow].filter(isDefined).map((i) => {
        const isTotalRow = i.id === totalRow?.id;
        return (
          <CarPdfRow key={i.id} paddingVertical={10}>
            {[
              <CarPdfViewCell
                key="label"
                style={{
                  paddingHorizontal: 5,
                  gap: 4,
                }}
              >
                <Svg
                  width={9}
                  height={9}
                  viewBox={"0 0 9 9"}
                  style={{ marginTop: 1 }}
                >
                  {!isTotalRow && (
                    <G>
                      <Rect
                        width={9}
                        height={9}
                        rx={4}
                        ry={4}
                        fill={theme.palette.gray6}
                      />
                      <Rect
                        width={7}
                        height={7}
                        x={1}
                        y={1}
                        rx={3}
                        ry={3}
                        fill={i.color}
                      />
                    </G>
                  )}
                </Svg>
                <CarPdfTypography
                  variant={isTotalRow ? "v11Semibold" : "v11Regular"}
                >
                  {i.name}
                </CarPdfTypography>
              </CarPdfViewCell>,
              ...i.modules.map((mod) => {
                const value = mod.values.find(
                  (val) => val.columnId === columnId,
                )?.allocation;
                return (
                  <CarPdfTextCell
                    key={mod.moduleId}
                    textVariant={isTotalRow ? "v11Semibold" : "v11Regular"}
                  >
                    {isDefined(value) ? formatPercent(value * 100, 1) : "-"}
                  </CarPdfTextCell>
                );
              }),
            ]}
          </CarPdfRow>
        );
      })}
    </CarPdfTable>
  );
};
